import { defineMessages } from 'react-intl';

import globalMessages from 'shared/global/messages';

const chatAgentMessages = defineMessages({
  editKnowledgeBase: {
    id: 'app.containers.ChatAgent.editKnowledgeBase',
    defaultMessage: 'Edit knowledge base',
    description: 'Open the information Candidate AI uses to answer applicants',
  },
  dateLast7Days: {
    id: 'app.containers.ChatAgent.dateLast7Days',
    defaultMessage: 'Last 7 days',
    description: 'Conversation date filter for the past week',
  },
  dateLast30Days: {
    id: 'app.containers.ChatAgent.dateLast30Days',
    defaultMessage: 'Last 30 days',
    description: 'Default conversation date filter for the past month',
  },
  dateLast90Days: {
    id: 'app.containers.ChatAgent.dateLast90Days',
    defaultMessage: 'Last 90 days',
    description: 'Conversation date filter for the past three months',
  },
  dateCustomRange: {
    id: 'app.containers.ChatAgent.dateCustomRange',
    defaultMessage: 'Custom range',
    description: 'Choose the first and last calendar days for conversations',
  },
  dateRangeMissing: {
    id: 'app.containers.ChatAgent.dateRangeMissing',
    defaultMessage: 'Pick a start date and an end date.',
    description:
      'Both bounds are required before applying a custom date filter',
  },
  dateRangeOrder: {
    id: 'app.containers.ChatAgent.dateRangeOrder',
    defaultMessage: 'Pick an end date on or after the start date.',
    description: 'The custom range ends before its first calendar day',
  },
  dateRangeFuture: {
    id: 'app.containers.ChatAgent.dateRangeFuture',
    defaultMessage: 'Pick dates on or before today.',
    description:
      'Conversation date filters cannot include future calendar days',
  },
  dateRangeTooWide: {
    id: 'app.containers.ChatAgent.dateRangeTooWide',
    defaultMessage: 'Pick a range of 90 days or less.',
    description: 'The custom conversation range exceeds 90 calendar days',
  },
  filterAllOpenings: {
    id: 'app.containers.ChatAgent.filterAllOpenings',
    defaultMessage: 'All openings',
    description:
      'Include conversations for every opening the reader may access',
  },
  searchOpenings: {
    id: 'app.containers.ChatAgent.searchOpenings',
    defaultMessage: 'Search openings',
    description: 'Search accessible hiring openings by title',
  },
  openingsLoadError: {
    id: 'app.containers.ChatAgent.openingsLoadError',
    defaultMessage: "We couldn't load openings. Try again.",
    description:
      'Opening search failed; the adjacent button retries the request',
  },
  noMatchingOpenings: {
    id: 'app.containers.ChatAgent.noMatchingOpenings',
    defaultMessage: 'No matching openings',
    description: 'No accessible opening title matches the search',
  },
  filterChannel: {
    id: 'app.containers.ChatAgent.filterChannel',
    defaultMessage: 'Channel',
    description: 'Filter conversations by their messaging medium',
  },
  channelAll: {
    id: 'app.containers.ChatAgent.channelAll',
    defaultMessage: 'All channels',
    description: 'Include website chat, SMS, WhatsApp, and email conversations',
  },
  emptyFiltered: {
    id: 'app.containers.ChatAgent.emptyFiltered',
    defaultMessage: 'No conversations match these filters',
    description:
      'No applicant question matches the active conversation filters',
  },
  clearFilters: {
    id: 'app.containers.ChatAgent.clearFilters',
    defaultMessage: 'Clear filters',
    description:
      'Restore the default conversation date window and remove other filters',
  },
  pickOneOpening: {
    id: 'app.containers.ChatAgent.pickOneOpening',
    defaultMessage: 'Pick an opening to see its conversations.',
    description:
      'The reader must narrow the list to one accessible hiring opening',
  },
  transcriptTitle: {
    id: 'app.containers.ChatAgent.transcriptTitle',
    defaultMessage: 'Transcript',
    description: 'Conversation between an applicant and Candidate AI',
  },
  transcriptApplicant: {
    id: 'app.containers.ChatAgent.transcriptApplicant',
    defaultMessage: 'Applicant · {time}',
    description: 'Applicant message sender and time',
  },
  transcriptCai: {
    id: 'app.containers.ChatAgent.transcriptCai',
    defaultMessage: 'CAI · {time}',
    description: 'Candidate AI message sender and time',
  },
  transcriptNoAccessTitle: {
    id: 'app.containers.ChatAgent.transcriptNoAccessTitle',
    defaultMessage: 'No access to this conversation',
    description: 'Conversation outside the reader’s permitted openings',
  },
  transcriptNoAccessBody: {
    id: 'app.containers.ChatAgent.transcriptNoAccessBody',
    defaultMessage:
      "It belongs to an opening you can't view. Ask an admin for access to that opening.",
    description: 'How to request access to the opening for a conversation',
  },
  transcriptEarlier: {
    id: 'app.containers.ChatAgent.transcriptEarlier',
    defaultMessage: 'Earlier messages not shown.',
    description:
      'Older conversation messages are outside the displayed excerpt',
  },
  transcriptLater: {
    id: 'app.containers.ChatAgent.transcriptLater',
    defaultMessage: 'Later messages not shown.',
    description:
      'Newer conversation messages are outside the displayed excerpt',
  },
  openApplicant: {
    id: 'app.containers.ChatAgent.openApplicant',
    defaultMessage: 'Open applicant',
    description: 'Navigate to the applicant profile for this conversation',
  },
  viewConversations: {
    id: 'app.containers.ChatAgent.viewConversations',
    defaultMessage: 'View conversations',
    description:
      'Open the applicant questions and answers for a Candidate AI agent',
  },
  richStepOpenings: {
    id: 'app.containers.ChatAgent.richStepOpenings',
    defaultMessage: 'Openings shown',
    description: 'Hiring openings Candidate AI presented to the applicant',
  },
  richStepOpeningsNearby: {
    id: 'app.containers.ChatAgent.richStepOpeningsNearby',
    defaultMessage: 'Openings near the applicant',
    description:
      'Candidate AI offered openings based on the applicant’s location',
  },
  richStepApplicationForm: {
    id: 'app.containers.ChatAgent.richStepApplicationForm',
    defaultMessage: 'Application form',
    description:
      'Candidate AI presented a form to collect application information',
  },
  richStepApplicationSubmitted: {
    id: 'app.containers.ChatAgent.richStepApplicationSubmitted',
    defaultMessage: 'Application submitted',
    description:
      'Candidate AI confirmed the applicant’s application submission',
  },
  transcriptStepPlaceholder: {
    id: 'app.containers.ChatAgent.transcriptStepPlaceholder',
    defaultMessage: '{stepName} step',
    description: 'Name of a Candidate AI action without a specialized preview',
  },
  conversationsTab: {
    id: 'app.containers.ChatAgent.conversationsTab',
    defaultMessage: 'Conversations',
    description:
      'Applicant questions and Candidate AI answers across an agent’s conversations',
  },
  conversationsBack: {
    id: 'app.containers.ChatAgent.conversationsBack',
    defaultMessage: 'Back to AI Agent setup',
    description: 'Return to the list of Candidate AI agents',
  },
  columnApplicant: {
    id: 'app.containers.ChatAgent.columnApplicant',
    defaultMessage: 'Applicant',
    description: 'Person who asked Candidate AI a question',
  },
  columnQuestion: {
    id: 'app.containers.ChatAgent.columnQuestion',
    defaultMessage: 'Applicant asked',
    description: 'Question the applicant sent to Candidate AI',
  },
  columnAnswer: {
    id: 'app.containers.ChatAgent.columnAnswer',
    defaultMessage: 'CAI answered',
    description: 'Answer Candidate AI gave to the applicant',
  },
  noAnswer: {
    id: 'app.containers.ChatAgent.noAnswer',
    defaultMessage: 'No answer',
    description: 'Candidate AI has no reply to this question',
  },
  visitorApplicant: {
    id: 'app.containers.ChatAgent.visitorApplicant',
    defaultMessage: 'Visitor',
    description: 'Person chatting without an associated applicant or opening',
  },
  anonymizedApplicant: {
    id: 'app.containers.ChatAgent.anonymizedApplicant',
    defaultMessage: 'Anonymized applicant',
    description:
      'Applicant whose identity and conversation content have been erased',
  },
  filterNoOpening: {
    id: 'app.containers.ChatAgent.filterNoOpening',
    defaultMessage: 'No opening',
    description: 'Conversation has no associated hiring opening',
  },
  unknownOpening: {
    id: 'app.containers.ChatAgent.unknownOpening',
    defaultMessage: 'Unknown opening',
    description: 'The conversation’s opening title is unavailable',
  },
  filterThumbsDown: {
    id: 'app.containers.ChatAgent.filterThumbsDown',
    defaultMessage: 'Thumbs down',
    description: 'Applicant marked this Candidate AI answer as unhelpful',
  },
  filterKnowledgeGap: {
    id: 'app.containers.ChatAgent.filterKnowledgeGap',
    defaultMessage: 'Knowledge gap',
    description:
      'Candidate AI lacks the information needed to answer this question',
  },
  channelWidget: {
    id: 'app.containers.ChatAgent.channelWidget',
    defaultMessage: 'Web chat',
    description: 'Conversation through the website chat widget',
  },
  channelSms: {
    id: 'app.containers.ChatAgent.channelSms',
    defaultMessage: 'SMS',
    description: 'Conversation through text messages',
  },
  channelWhatsapp: {
    id: 'app.containers.ChatAgent.channelWhatsapp',
    defaultMessage: 'WhatsApp',
    description: 'Conversation through WhatsApp messages',
  },
  emptyNoConversationsTitle: {
    id: 'app.containers.ChatAgent.emptyNoConversationsTitle',
    defaultMessage: 'No conversations in this period',
    description:
      'No applicant questions are available for the selected time period',
  },
  emptyNoConversationsBody: {
    id: 'app.containers.ChatAgent.emptyNoConversationsBody',
    defaultMessage:
      'Applicant questions appear here once CAI starts answering.',
    description: 'Explains when the conversation list will contain questions',
  },
  loadError: {
    id: 'app.containers.ChatAgent.loadError',
    defaultMessage: "We couldn't load conversations.",
    description:
      'Conversation retrieval failed; the adjacent Try again button retries it',
  },
  endOfList: {
    id: 'app.containers.ChatAgent.endOfList',
    defaultMessage: 'No older conversations in this period.',
    description: 'All conversation turns in the time period are displayed',
  },
  title: {
    id: 'app.containers.ChatAgent.title',
    defaultMessage: 'Fountain AI',
  },
  header: {
    id: 'app.containers.ChatAgent.header',
    defaultMessage: 'Candidate AI Agent',
  },
  aiAgentSetup: {
    id: 'app.containers.ChatAgent.aiAgentSetup',
    defaultMessage: 'AI Agent setup',
  },
  aiRecommendations: {
    id: 'app.containers.ChatAgent.aiRecommendations',
    defaultMessage: 'AI recommendations',
  },
  setupFirstWidget: {
    id: 'app.containers.ChatAgent.setupFirstWidget',
    defaultMessage: 'Setup your first AI Agent',
  },
  setupDescription: {
    id: 'app.containers.ChatAgent.setupDescription',
    defaultMessage:
      'Candidate AI Agent is a Fountain’s AI-powered tool designed to engage and assist candidates during their application process. You can follow the steps below to setup your AI Agent:',
    description:
      'Explains that Candidate AI Agent helps candidates during applications and introduces the steps for configuring it',
  },
  setupAiAgent: {
    id: 'app.containers.ChatAgent.setupAiAgent',
    defaultMessage: 'Set up AI Agent',
  },
  uploadKnowledgeBase: {
    id: 'app.containers.ChatAgent.uploadKnowledgeBase',
    defaultMessage: 'Upload knowledge base',
  },
  uploadKnowledgeBaseInstruction: {
    id: 'app.containers.ChatAgent.uploadKnowledgeBaseInstruction',
    defaultMessage:
      'Upload a .csv file to train the chatbot to answer questions that are not found from the existing Fountain data (ie. Job Description, Position, Location, Opening Details, and Workflow).',
  },
  sampleKnowledgeBaseInstruction: {
    id: 'app.containers.ChatAgent.sampleKnowledgeBaseInstruction',
    defaultMessage: `Use this <templateFileLink>template file</templateFileLink> to upload a .csv file of the knowledge base in the format of question and answer. For best practices on creating your knowledge base, please refer to the <helpCenterLink>Help Center</helpCenterLink> article.`,
  },
  setAIAgentFunctionalities: {
    id: 'app.containers.ChatAgent.setAIAgentFunctionalities',
    defaultMessage: 'Set AI Agent Functionalities',
  },
  aiAgentVisibility: {
    id: 'app.containers.ChatAgent.aiAgentVisibility',
    defaultMessage: 'AI Agent visibility',
  },
  aiAgentVisibilityInstruction: {
    id: 'app.containers.ChatAgent.aiAgentVisibilityInstruction',
    defaultMessage:
      'Select which platforms to enable the AI Agent to assist applicants.',
  },
  fallbackInteractions: {
    id: 'app.containers.ChatAgent.fallbackInteractions',
    defaultMessage: 'Fallback contact',
  },
  fallbackInteractionsInstruction: {
    id: 'app.containers.ChatAgent.fallbackInteractionsInstruction',
    defaultMessage:
      'Customize a fallback interaction method to sustain a conversation when the AI Agent can’t respond to an applicant.',
  },
  contactInfo: {
    id: 'app.containers.ChatAgent.contactInfo',
    defaultMessage: 'Contact info',
  },
  contactInfoPlaceholder: {
    id: 'app.containers.ChatAgent.contactInfoPlaceholder',
    defaultMessage: 'Enter phone number, email or website',
  },
  personalizeAIAgent: {
    id: 'app.containers.ChatAgent.personalizeAIAgent',
    defaultMessage: 'Personalize AI Agent',
  },
  chooseBrand: {
    id: 'app.containers.ChatAgent.chooseBrand',
    defaultMessage: 'Choose brand',
  },
  chooseBrandDescription: {
    id: 'app.containers.ChatAgent.chooseBrandDescription',
    defaultMessage:
      "Choose a brand to apply it's logo and color to the appearance of the widget",
  },
  selectBrandPlaceholder: {
    id: 'app.containers.ChatbotWidget.selectBrandPlaceholder',
    defaultMessage: 'Please select Brand',
  },
  brand: {
    id: 'app.containers.ChatAgent.brand',
    defaultMessage: 'Brand',
  },
  aiAgentName: {
    id: 'app.containers.ChatAgent.aiAgentName',
    defaultMessage: 'AI Agent name',
  },
  aiAgentNameRequired: {
    id: 'app.containers.ChatAgent.aiAgentNameRequired',
    defaultMessage: 'AI Agent name is required.',
  },
  visibilityIsRequired: {
    id: 'app.containers.ChatAgent.visibilityIsRequired',
    defaultMessage: 'At least one visibility option must be selected.',
  },
  chatbotName: {
    id: 'app.containers.ChatAgent.chatbotName',
    defaultMessage: 'Chatbot name',
  },
  chatbotNamePlaceholder: {
    id: 'app.containers.ChatAgent.chatbotNamePlaceholder',
    defaultMessage: 'Ex. Ava, Luna, Maya, Oliver, Milo',
  },
  agentPublishSuccess: {
    id: 'app.containers.ChatAgent.agentPublishSuccess',
    defaultMessage: 'Agent published successfully',
  },
  agentUnPublishSuccess: {
    id: 'app.containers.ChatAgent.agentUnPublishSuccess',
    defaultMessage: 'Agent Unpublished successfully',
  },
  agentPublishFailed: {
    id: 'app.containers.ChatAgent.agentPublishFailed',
    defaultMessage: 'Failed to publish Agent',
  },
  agentUnPublishFailed: {
    id: 'app.containers.ChatAgent.agentUnPublishFailed',
    defaultMessage: 'Failed to Unpublish Agent',
  },
  agentSaveSuccess: {
    id: 'app.containers.ChatAgent.agentSaveSuccess',
    defaultMessage: 'Agent saved successfully',
  },
  agentSaveFailed: {
    id: 'app.containers.ChatAgent.agentSaveFailed',
    defaultMessage: 'Failed to save Agent',
  },
  fetchAccessTokenFailed: {
    id: 'app.containers.ChatAgent.fetchAccessTokenFailed',
    defaultMessage: 'Failed to fetch Access Token',
  },
  uploadFileSuccess: {
    id: 'app.containers.ChatAgent.uploadFileSuccess',
    defaultMessage: 'File uploaded successfully',
  },
  uploadFileFailed: {
    id: 'app.containers.ChatAgent.uploadFileFailed',
    defaultMessage: 'Failed to upload file',
  },
  csvStructuralError: {
    id: 'app.containers.ChatAgent.csvStructuralError',
    defaultMessage:
      'Knowledge base CSV structure is invalid. Required headers: {requiredHeaders}. Optional headers: {optionalHeaders}.',
  },
  downloadFileSuccess: {
    id: 'app.containers.ChatAgent.downloadFileSuccess',
    defaultMessage: 'File downloaded successfully',
  },
  downloadFileFailed: {
    id: 'app.containers.ChatAgent.downloadFileFailed',
    defaultMessage: 'Failed to download file',
  },
  fetchKnowledgeBaseFailed: {
    id: 'app.containers.ChatAgent.fetchKnowledgeBaseFailed',
    defaultMessage: 'Failed to fetch knowledge base',
  },
  fetchAgentsFailed: {
    id: 'app.containers.ChatAgent.fetchAgentsFailed',
    defaultMessage: 'Failed to fetch agents',
  },
  candidateAiAgent: {
    id: 'app.containers.ChatAgent.candidateAiAgent',
    defaultMessage: 'Candidate AI Agent',
  },
  agentCreationFailed: {
    id: 'app.containers.ChatAgent.agentCreationFailed',
    defaultMessage: 'Failed to create Agent',
  },
  agentUpdateFailed: {
    id: 'app.containers.ChatAgent.agentUpdateFailed',
    defaultMessage: 'Failer to update Agent',
  },
  phoneNumber: {
    id: 'app.containers.ChatAgent.phoneNumber',
    defaultMessage: 'Phone Number',
  },
  website: {
    id: 'app.containers.ChatAgent.website',
    defaultMessage: 'Website',
  },
  fallbackMethod: {
    id: 'app.containers.ChatAgent.fallbackMethod',
    defaultMessage: 'Fallback method',
  },
  copiedToClipboard: {
    id: 'app.containers.ChatAgent.copiedToClipboard',
    defaultMessage: 'Copied to clipboard',
  },
  failedToCopy: {
    id: 'app.containers.ChatAgent.failedToCopy',
    defaultMessage: 'Failed to copy',
  },
  agentDeleteSuccess: {
    id: 'app.containers.ChatAgent.agentDeleteSuccess',
    defaultMessage: 'Agent deleted successfully',
  },
  agentDeleteFailed: {
    id: 'app.containers.ChatAgent.agentDeleteFailed',
    defaultMessage: 'Failed to delete agent',
  },
  noBrandAvailableForAgent: {
    id: 'app.containers.ChatAgent.noBrandAvailableForAgent',
    defaultMessage: 'No more brand is available to set up AI Agent',
  },
  allBrandAvailableForAgent: {
    id: 'app.containers.ChatAgent.allBrandAvailableForAgent',
    defaultMessage:
      'An AI Agent for "All Brand" exist. To add multiple AI agents, update the existing AI agent’s brand selection to “Individual brand".',
  },
  fountainWebsiteDescription: {
    id: 'app.containers.ChatAgent.fountainWebsiteDescription',
    defaultMessage:
      'AI Agent will be enabled across the Job Directory and Applicant Portal.',
  },
  smsEnabledDescription: {
    id: 'app.containers.ChatAgent.smsEnabledDescription',
    defaultMessage: 'AI Agent will be enabled to respond to inquires via SMS.',
  },
  smsWhatsappEnabledDescription: {
    id: 'app.containers.ChatAgent.smsWhatsappEnabledDescription',
    defaultMessage:
      'AI Agent will be enabled in Fountain SMS or WhatsApp, per applicant’s preferred communication choice.',
  },
  allBrandDisableMessage: {
    id: 'app.containers.ChatAgent.allBrandDisableMessage',
    defaultMessage:
      "To create an AI Agent for 'All Brands', remove any existing AI Agent that is set up for individual brand.",
  },
  allBrandDescription: {
    id: 'app.containers.ChatAgent.allBrandDescription',
    defaultMessage:
      "If 'All brands' is selected, the brand name, the color and logo of the default brand will be applied to the widget.",
  },
  individualBrandDescription: {
    id: 'app.containers.ChatAgent.individualBrandDescription',
    defaultMessage:
      'The color and logo of the selected brand will be applied to the widget.',
  },
  deleteAiAgent: {
    id: 'app.containers.ChatAgent.deleteAiAgent',
    defaultMessage: 'Delete AI Agent',
  },
  deleteModalDescription: {
    id: 'app.containers.ChatAgent.deleteModalDescription',
    defaultMessage:
      "Are you sure you want to delete this AI Agent? This action can't be undone.",
  },
  publishAiAgent: {
    id: 'app.containers.ChatAgent.publishAiAgent',
    defaultMessage: 'Publish AI Agent',
  },
  publishAiAgentDescription: {
    id: 'app.containers.ChatAgent.publishAiAgentDescription',
    defaultMessage: 'Are you sure you want to Publish this AI Agent?',
  },
  unPublishAiAgent: {
    id: 'app.containers.ChatAgent.unPublishAiAgent',
    defaultMessage: 'Unpublish AI Agent',
  },
  unPublishAiAgentDescription: {
    id: 'app.containers.ChatAgent.unPublishAiAgentDescription',
    defaultMessage: 'Are you sure you want to Unpublish this AI Agent?',
  },
  unsavedModalDescription: {
    id: 'app.containers.ChatAgent.unsavedModalDescription',
    defaultMessage:
      'You have unsaved changes on this page, which will be lost if you don’t save. Do you want to continue editing or discard the changes?',
  },
  discard: {
    id: 'app.containers.ChatAgent.discard',
    defaultMessage: 'Discard',
  },
  continueEditing: {
    id: 'app.containers.ChatAgent.continueEditing',
    defaultMessage: 'Continue editing',
  },
  customWebsiteDescription: {
    id: 'app.containers.ChatAgent.customWebsiteDescription',
    defaultMessage:
      'AI Agent can be installed on the domain(s) you add below. Make sure to get the embed code and copy it to the domain(s) you provided.',
  },
  websiteDomain: {
    id: 'app.containers.ChatAgent.websiteDomain',
    defaultMessage: 'Website domain',
  },
  enterValidUrl: {
    id: 'app.containers.ChatAgent.enterValidUrl',
    defaultMessage: 'Please enter a valid URL',
  },
  deleteWebsiteDomain: {
    id: 'app.containers.ChatAgent.deleteWebsiteDomain',
    defaultMessage: 'Delete website domain',
  },
  deleteWebsiteDomainConfirmation: {
    id: 'app.containers.ChatAgent.deleteWebsiteDomainConfirmation',
    defaultMessage:
      'Are you sure you want to delete this website domain? The widget will be automatically removed from the website',
  },
  getEmbedCode: {
    id: 'app.containers.ChatAgent.getEmbedCode',
    defaultMessage: 'Get embed code',
  },
  getEmbedCodeDesc: {
    id: 'app.containers.ChatAgent.getEmbedCodeDesc',
    defaultMessage: 'Copy and paste this code to the domain(s) you provided.',
  },
  copyCode: {
    id: 'app.containers.ChatAgent.copyCode',
    defaultMessage: 'copy code',
  },
  agentFunctionTitle: {
    id: 'app.containers.ChatAgent.agentFunctionTitle',
    defaultMessage: 'Customize AI Agent function',
  },
  agentFunctionDesc: {
    id: 'app.containers.ChatAgent.agentFunctionDesc',
    defaultMessage: 'Select how you want the AI Agent to assist candidates.',
  },
  applyBotTitle: {
    id: 'app.containers.ChatAgent.applyBotTitle',
    defaultMessage:
      'Help candidate find a job and answer questions (recommended)',
  },
  applyBotDesc: {
    id: 'app.containers.ChatAgent.applyBotDesc',
    defaultMessage:
      'The AI Agent will display jobs for candidates to apply to and answer any questions they have about the job or application process.',
  },
  applyFaqBotTitle: {
    id: 'app.containers.ChatAgent.applyFaqBotTitle',
    defaultMessage: 'Answer candidate questions only',
  },
  applyFaqBotDesc: {
    id: 'app.containers.ChatAgent.applyFaqBotDesc',
    defaultMessage:
      'AI Agent will assist candidates with their application, application status, scheduling management, and answering questions from the knowledge base.',
  },
  testAiAgent: {
    id: 'app.containers.ChatAgent.testAiAgent',
    defaultMessage: 'Test AI Agent',
  },
  agentListIncomplete: {
    id: 'app.containers.ChatAgent.agentListIncomplete',
    defaultMessage:
      "We couldn't load every AI agent, so this list may be incomplete. Refresh the page or try again later.",
    description:
      'Warning shown above the list of candidate AI agents when some agents could not be loaded',
  },
  applyBotDisabled: {
    id: 'app.containers.ChatAgent.applyBotDisabled',
    defaultMessage: 'Please select a valid Bot Type',
  },
  noJobAvailableTooltip: {
    id: 'app.containers.ChatAgent.noJobAvailableTooltip',
    defaultMessage:
      'This option is disabled because 0 job is available in your account.',
  },
  oneJobAvailableTooltip: {
    id: 'app.containers.ChatAgent.oneJobAvailableTooltip',
    defaultMessage:
      'This option is disabled because there is only one job in your account.',
  },
  moreThanOneJobAvailableTooltip: {
    id: 'app.containers.ChatAgent.moreThanOneJobAvailableTooltip',
    defaultMessage:
      'This option is disabled because more than one job is available in your account.',
  },
  duplicateSettingCannotApplyAgain: {
    id: 'app.containers.ChatAgent.duplicateSettingCannotApplyAgain',
    defaultMessage:
      "Duplicate Rules setting is set to 'Cannot apply again’, which conflicts with AI Agent access to individual brand.",
  },
  jobsDirectoryCareerSiteUrl: {
    id: 'app.containers.ChatAgent.jobsDirectoryCareerSiteUrl',
    defaultMessage: 'Jobs Directory/Career site URL',
  },
  jobsDirectoryCareerSiteUrlInstruction: {
    id: 'app.containers.ChatAgent.jobsDirectoryCareerSiteUrlInstruction',
    defaultMessage:
      'Provide a career site or Jobs Directory URL you wish to display to applicants if they want to apply for a new job using the AI Agent from the Applicant Portal.',
  },
  invalidCareerSiteUrl: {
    id: 'app.containers.ChatAgent.invalidCareerSiteUrl',
    defaultMessage: 'Please enter a valid Career Site Url',
  },
  createConversationallyKb: {
    id: 'app.containers.ChatAgent.createConversationallyKb',
    defaultMessage: 'Create a Knowledge Base with AI Assistant',
  },
  createConversationallyKbDesc: {
    id: 'app.containers.ChatAgent.createConversationallyKbDesc',
    defaultMessage:
      'Fountain’s AI assistant helps you build a robust Knowledge Base by transforming uploaded materials (e.g., employee training guides, candidate FAQs) into its foundation. It then conversationally guides you to fill in any missing pieces, ensuring comprehensive coverage.',
  },
  uploadKbCsvFile: {
    id: 'app.containers.ChatAgent.uploadKbCsvFile',
    defaultMessage: 'Upload Knowledge Base',
  },
  uploadKbCsvFileDesc: {
    id: 'app.containers.ChatAgent.uploadKbCsvFileDesc',
    defaultMessage:
      'This option is ideal for those who know exactly what to include in a Knowledge Base. A template will be provided to ensure you are uploading a .csv file with the required format.',
  },
  trainAiAgent: {
    id: 'app.containers.ChatAgent.trainAiAgent',
    defaultMessage: 'Train AI Agent',
  },
  chooseKbMethod: {
    id: 'app.containers.ChatAgent.chooseKbMethod',
    defaultMessage: 'Choose a method to build your Knowledge Base.',
  },
  chooseKbMethodDesc: {
    id: 'app.containers.ChatAgent.chooseKbMethodDesc',
    defaultMessage:
      'A Knowledge Base provides supplemental information to the job description to train the AI Agent in addressing candidate inquiries about the company, policies, application process, troubleshooting, and more. <helpCenterLink>Learn more</helpCenterLink> about the AI Agent’s knowledge capabilities.',
  },
  defaultLanguage: {
    id: 'app.containers.ChatAgent.defaultLanguage',
    defaultMessage: 'Default Language',
  },
  defaultLanguageDesc: {
    id: 'app.containers.ChatAgent.defaultLanguageDesc',
    defaultMessage:
      "In cases where we are not able to detect the candidate's device language, please specify a default language the AI agent will use to initiate the conversation.",
  },
  selectDefaultLanguage: {
    id: 'app.containers.ChatAgent.selectDefaultLanguage',
    defaultMessage: 'Select a default language',
  },
  recommended: {
    id: 'app.containers.ChatAgent.recommended',
    defaultMessage: 'Recommended',
  },
  changesSavedSuccess: {
    id: 'app.containers.ChatAgent.changesSavedSuccess',
    defaultMessage: 'Changes Saved Successfully',
  },
  changesSavedFailed: {
    id: 'app.containers.ChatAgent.changesSavedFailed',
    defaultMessage: 'Failed to save changes',
  },
  publish: {
    id: 'app.containers.ChatAgent.publish',
    defaultMessage: 'Publish',
  },
  unPublish: {
    id: 'app.containers.ChatAgent.unPublish',
    defaultMessage: 'Unpublish',
  },
  saveAndNext: {
    id: 'app.containers.ChatAgent.saveAndNext',
    defaultMessage: 'Save and Next',
  },
  publishAndNext: {
    id: 'app.containers.ChatAgent.publishAndNext',
    defaultMessage: 'Publish and Next',
  },
  saveAsDraft: {
    id: 'app.containers.ChatAgent.saveAsDraft',
    defaultMessage: 'Save as Draft',
  },
  previous: {
    id: 'app.containers.ChatAgent.previous',
    defaultMessage: 'Previous',
  },
  agentContextTitle: {
    id: 'app.containers.ChatAgent.agentContextTitle',
    defaultMessage: 'Agent Knowledge base',
  },
  agentContextDescription: {
    id: 'app.containers.ChatAgent.agentContextDescription',
    defaultMessage:
      'Upload a document to provide context for your AI Agent. This helps the agent answer candidate questions accurately.',
  },
  whatDocumentsWorkBest: {
    id: 'app.containers.ChatAgent.whatDocumentsWorkBest',
    defaultMessage: 'What types of documents work best?',
  },
  skipUpload: {
    id: 'app.containers.ChatAgent.skipUpload',
    defaultMessage: 'Skip for now',
  },
  uploadFiles: {
    id: 'app.containers.ChatAgent.uploadFiles',
    defaultMessage: 'Upload files',
  },
  exportKnowledgeBase: {
    id: 'app.containers.ChatAgent.exportKnowledgeBase',
    defaultMessage: 'Export',
  },
  addTopic: {
    id: 'app.containers.ChatAgent.addTopic',
    defaultMessage: 'Add topic',
  },
  topicsComplete: {
    id: 'app.containers.ChatAgent.topicsComplete',
    defaultMessage: '{count}/{total} topics complete',
  },
  approveCopy: {
    id: 'app.containers.ChatAgent.approveCopy',
    defaultMessage: 'Approve copy',
  },
  exampleApplicantQuestions: {
    id: 'app.containers.ChatAgent.exampleApplicantQuestions',
    defaultMessage: 'Example applicant questions',
  },
  knowledgeBaseEditor: {
    id: 'app.containers.ChatAgent.knowledgeBaseEditor',
    defaultMessage: 'Knowledge base',
  },
  dragAndDropContext: {
    id: 'app.containers.ChatAgent.dragAndDropContext',
    defaultMessage:
      'Drag and drop or click to upload (.pdf, .ppt, .docx, .txt, .csv)',
  },
  uploadingFile: {
    id: 'app.containers.ChatAgent.uploadingFile',
    defaultMessage: 'Uploading...',
  },
  topicContentPlaceholder: {
    id: 'app.containers.ChatAgent.topicContentPlaceholder',
    defaultMessage: 'Add context about this topic...',
  },
  topicApproveRequiresContent: {
    id: 'app.containers.ChatAgent.topicApproveRequiresContent',
    defaultMessage: 'Add content before approving this topic',
  },
  topicApproved: {
    id: 'app.containers.ChatAgent.topicApproved',
    defaultMessage: 'Topic approved',
  },
  topicUnapproved: {
    id: 'app.containers.ChatAgent.topicUnapproved',
    defaultMessage: 'Topic approval removed',
  },
  processingBannerText: {
    id: 'app.containers.ChatAgent.processingBannerText',
    defaultMessage:
      'Your document is being processed — topics will populate shortly. You can continue editing in the meantime.',
  },
  processingBannerTextPaused: {
    id: 'app.containers.ChatAgent.processingBannerTextPaused',
    defaultMessage:
      'Our AI is processing the uploaded files to build the knowledge base. Editing is paused until this completes.',
  },
  processingBannerSuccess: {
    id: 'app.containers.ChatAgent.processingBannerSuccess',
    defaultMessage: 'Knowledge base updated',
  },
  topicNamePlaceholder: {
    id: 'app.containers.ChatAgent.topicNamePlaceholder',
    defaultMessage: 'Topic name',
  },
  deleteTopic: {
    id: 'app.containers.ChatAgent.deleteTopic',
    defaultMessage: 'Delete topic',
  },
  deleteTopicConfirmation: {
    id: 'app.containers.ChatAgent.deleteTopicConfirmation',
    defaultMessage:
      'Are you sure you want to delete “{topicName}”? Its content will be removed and this action cannot be undone.',
  },
  topicRenameFailed: {
    id: 'app.containers.ChatAgent.topicRenameFailed',
    defaultMessage: 'Failed to rename topic',
  },
  topicDeleteFailed: {
    id: 'app.containers.ChatAgent.topicDeleteFailed',
    defaultMessage: 'Failed to delete topic',
  },
  noFilesUploaded: {
    id: 'app.containers.ChatAgent.noFilesUploaded',
    defaultMessage: 'No files uploaded yet.',
  },
  uploadedFilesTitle: {
    id: 'app.containers.ChatAgent.uploadedFilesTitle',
    defaultMessage: 'Uploaded files',
  },
  statusProcessing: {
    id: 'app.containers.ChatAgent.statusProcessing',
    defaultMessage: 'Processing',
  },
  statusReady: {
    id: 'app.containers.ChatAgent.statusReady',
    defaultMessage: '✓ Ready',
  },
  statusFailed: {
    id: 'app.containers.ChatAgent.statusFailed',
    defaultMessage: 'Failed',
  },
  restoreFailed: {
    id: 'app.containers.ChatAgent.restoreFailed',
    defaultMessage: 'Failed to restore version',
  },
  viewVersionHistory: {
    id: 'app.containers.ChatAgent.viewVersionHistory',
    defaultMessage: 'View version history',
  },
  hideVersionHistory: {
    id: 'app.containers.ChatAgent.hideVersionHistory',
    defaultMessage: 'Hide version history',
  },
  startDate: {
    id: 'app.containers.ChatAgent.startDate',
    defaultMessage: 'Conversations from',
    description:
      'Label for the start of the date range being analyzed (not a generic "start date" field)',
  },
  endDate: {
    id: 'app.containers.ChatAgent.endDate',
    defaultMessage: 'Conversations to',
    description:
      'Label for the end of the date range being analyzed (not a generic "end date" field)',
  },
});

export const messages = {
  ...chatAgentMessages,
  filterOpening: globalMessages.opening,
  channelEmail: globalMessages.email,
  email: globalMessages.email,
  richStepQuestion: globalMessages.question,
};
